// The sixth route: `client-rounds-standings` (ported from
// `prototype/sketches/rounds-client/standings.html`, option 1 — decided over a rounds mode of
// `/summary` at prototype review, 2026-08-03, on rendered evidence: a segmented control switching
// subject on invisible provenance is exactly the pattern UI.md §3 argues against, and it would drag
// live mode's `Summary` into every future rounds change for a tab that stays empty the whole
// tournament).
//
// A **document, not a canvas**: paper ground, no world view, no transport. Two things make this
// screen the *inverse* of every other rounds surface, and both are load-bearing rather than
// decorative:
//
//   * **Placement language is required here, not forbidden.** Every other screen that shows a score
//     either forbids rank wording outright (`client-sandbox-screen`'s sandbox runs) or carries it only
//     for a graded round it is already looking at (`client-rounds-review`). This is the one place a
//     player reads *where they stand* across the whole tournament, so the mean and each round's score
//     are shown next to an explicit rank/placement — never a bare number.
//   * **The mean is never a client computation.** `mean` and `rank` come from `rounds-standings.ts`
//     verbatim; `gradedRounds` rides beside them so the header and the note can *label* the
//     denominator without inventing one — dividing by `roundScores.length` would silently contradict
//     the server the moment a defaulted round and a missed one stopped coinciding.
//
// This screen renders **no sandbox score anywhere** — every number here is a graded round's score,
// the G2 mean, or a placement, all sourced from the standings read alone (design.md **D5**: "standings
// screen | standings source", nothing else). That is what keeps the two kinds of score from ever being
// confused: there is nothing here that *could* be mislabelled, because nothing sandbox-shaped is
// rendered at all. The one "sandbox" word on the screen is the Back control, which names a
// destination, never a score.
//
// Every seam is optional with an inert default, matching every other route: a bare mount renders a
// real, honest state — "standings are not connected" — rather than crashing or, worse, a leaderboard
// of fabricated zeros.

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  scoreForRound,
  type MemberStanding,
  type Standings as StandingsBoard,
  type StandingsRead,
  type StandingsSource,
} from '../../net/rounds-standings';
import { deriveColonyRoster } from '../rounds/colony-placement';
import { groupDigits } from '../rounds/review-model';

export interface StandingsProps {
  /** The G2 read. Optional with an inert default (see {@link INERT_STANDINGS_SOURCE}). */
  source?: StandingsSource;
  /**
   * The caller's own join ordinal, exactly as the phase read states it. `null` — the default — is
   * the honest degraded state: no row is highlighted as "you", and the per-round cards show no own
   * score or medal, rather than guessing which member is the player.
   */
  ownJoinOrdinal?: number | null;
  /**
   * The caller's own colony id, exactly as the phase read states it — the other half of design
   * **D4**'s cross-check (`ui/rounds/colony-placement.ts`'s `deriveColonyRoster`). `null` — the
   * default — means the per-round medal renders its placeholder rather than asserting a placement
   * it cannot verify: `ownPlacementFor` returns `null` whenever `ownColonyId` is `null`, by design.
   */
  ownColonyId?: number | null;
  /**
   * Round numbers whose **detail** tier has been reclaimed by retention.
   *
   * Optional and additive only — design.md's Non-Goal is explicit that this change defines "no
   * second `evicted` descriptor" of its own. With none supplied, no round states a retention
   * condition; the one signal itself is owned by the trace-serving layer, and a later change wires
   * it through once this screen is composed with a trace reader. A round's placement and its coarse
   * playback are unaffected either way — this prop only controls whether the card *says so*.
   */
  evictedRounds?: readonly number[];
  /**
   * Opens the graded review screen on `roundNo`. Optional and inert by default: this screen never
   * imports a router — navigation, and restoring the surface the player came from, are the
   * composition root's (design **D5** / **D8**).
   */
  onWatchRound?(roundNo: number): void;
  /** Returns to the surface the player came from. Optional and inert by default, for the same
   * reason: Standings only ever asks to leave. */
  onBack?(): void;
}

/** A round number too large to ever collide with a real round — used only as `key`/`data-testid`
 * suffix material, never compared against. */
const NO_EVICTED_ROUNDS: readonly number[] = [];

/**
 * The inert default. `StandingsRead` has exactly three arms — `ready` / `noGradedRound` / `failed`
 * — and a bare mount has no data of any kind, so `failed` is the honest one: it degrades to a named
 * state precisely as the requirement demands, rather than a `ready` board with nothing in it.
 */
const INERT_STANDINGS_SOURCE: StandingsSource = {
  read: async () => ({ kind: 'failed', message: 'standings are not connected' }),
};

/** 1st/2nd/3rd/4th/11th… — the standard English ordinal suffix, the placement wording this whole
 * screen exists to use. */
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** The competing roster, ordered by the server's own tournament-wide `rank` — never re-derived,
 * ties keeping the server's order and breaking by ascending `joinOrdinal` (join order) only when
 * `rank` itself ties, so the on-screen order is stable. */
function orderedCompetitors(members: readonly MemberStanding[]): MemberStanding[] {
  return members
    .filter((member) => member.competing)
    .slice()
    .sort((a, b) => {
      if (a.rank === b.rank) {
        return a.joinOrdinal - b.joinOrdinal;
      }
      if (a.rank === null) {
        return 1;
      }
      if (b.rank === null) {
        return -1;
      }
      return a.rank - b.rank;
    });
}

export function Standings({
  source = INERT_STANDINGS_SOURCE,
  ownJoinOrdinal = null,
  ownColonyId = null,
  evictedRounds = NO_EVICTED_ROUNDS,
  onWatchRound,
  onBack,
}: StandingsProps = {}) {
  const [read, setRead] = useState<StandingsRead | null>(null);

  // Read once, on mount (and again if the composition root ever hands down a different source
  // instance). Re-reading on a phase/round change that could have graded something is design D1's
  // rule for the *caller* — this component only ever asks once per source it is given.
  useEffect(() => {
    let cancelled = false;
    setRead(null);
    void source.read().then((result) => {
      if (!cancelled) {
        setRead(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const evicted = useMemo(() => new Set(evictedRounds), [evictedRounds]);

  const gradedRounds = read !== null && read.kind !== 'failed' ? read.standings.gradedRounds : null;

  return (
    <div data-testid="standings-screen" style={SCREEN_STYLE}>
      <div data-testid="standings-header" style={HEADER_STYLE}>
        <span style={KICKER_STYLE}>
          Standings
          {gradedRounds !== null
            ? ` · ${gradedRounds} graded round${gradedRounds === 1 ? '' : 's'}`
            : ''}
        </span>
        <h2 style={TITLE_STYLE}>The meadow so far</h2>
        <p style={SUBTITLE_STYLE}>Mean living bees across graded rounds</p>
      </div>

      <div data-testid="standings-body" style={BODY_STYLE}>
        {read === null && (
          <p data-testid="standings-loading" style={NOTE_STYLE}>
            Reading standings…
          </p>
        )}

        {read !== null && read.kind === 'failed' && (
          <div data-testid="standings-failed" role="alert" style={ALERT_STYLE}>
            <b style={ALERT_TITLE_STYLE}>The standings couldn’t be read.</b>
            <span>{read.message}</span>
          </div>
        )}

        {read !== null && read.kind === 'noGradedRound' && (
          <div data-testid="standings-no-graded-round" style={EMPTY_STATE_STYLE}>
            {/* The centered muted pill every other empty state in the kit uses
                (`SandboxScreen`'s empty-world hint) — not a floating line of body text. */}
            <span style={EMPTY_STATE_PILL_STYLE}>
              No round has been graded yet — standings appear once round 1 finishes.
            </span>
          </div>
        )}

        {read !== null && read.kind === 'ready' && (
          <StandingsReady
            standings={read.standings}
            ownJoinOrdinal={ownJoinOrdinal}
            ownColonyId={ownColonyId}
            evicted={evicted}
            onWatchRound={onWatchRound}
          />
        )}
      </div>

      <div data-testid="standings-back-bar" style={BACK_BAR_STYLE}>
        <button
          type="button"
          data-testid="standings-back-action"
          style={BACK_BUTTON_STYLE}
          onClick={() => onBack?.()}
        >
          Back to the sandbox
        </button>
      </div>
    </div>
  );
}

interface StandingsReadyProps {
  standings: StandingsBoard;
  ownJoinOrdinal: number | null;
  ownColonyId: number | null;
  evicted: ReadonlySet<number>;
  onWatchRound?(roundNo: number): void;
}

function StandingsReady({
  standings,
  ownJoinOrdinal,
  ownColonyId,
  evicted,
  onWatchRound,
}: StandingsReadyProps) {
  const competitors = orderedCompetitors(standings.members);
  // A spectator has no competing standing at all (never a fabricated zero, never a mean over no
  // rounds) — distinguished into its own list rather than a row of the ranked table.
  const spectators = standings.members.filter((member) => !member.competing);
  const roundNumbers = Array.from({ length: standings.gradedRounds }, (_, index) => index + 1);
  // Design **D4**'s colony roster — not a local re-derivation. `ownPlacementFor` is the SAME
  // computation task 13.2's Review-phase result card uses for "the player's own placement in a
  // round" (both cross-check `ownColonyId` against `ownJoinOrdinal` the identical way, so a tie
  // and a fail-safe drop names look the same on both surfaces). This screen renders no colony
  // number anywhere and never reads `roster.colonies`/`namesTrusted` — the table already has real
  // member names straight from the standings read — it consumes the roster purely for the one
  // placement number the round cards below need. Do not re-introduce a local per-round ranking
  // here: two implementations of one specified rule is exactly the drift D4's cross-check exists
  // to prevent.
  const roster = deriveColonyRoster({ standings, ownColonyId, ownJoinOrdinal });

  return (
    <>
      <div data-testid="standings-table" style={TABLE_STYLE}>
        <div style={TABLE_HEAD_ROW_STYLE}>
          <span style={{ ...COL_HEAD_STYLE, width: 30, flexShrink: 0 }} />
          <span style={{ ...COL_HEAD_STYLE, flex: 1 }}>Player</span>
          {roundNumbers.map((roundNo) => (
            <span key={roundNo} style={{ ...COL_HEAD_STYLE, width: 40, flexShrink: 0, textAlign: 'right' }}>
              R{roundNo}
            </span>
          ))}
          {/* Labelled as a mean **over graded rounds**, never as a sum and never over an invented
              denominator (`client-rounds-standings`'s G2 rule) — the header names what the column
              is, and the note below states the denominator in full sentences. */}
          <span style={{ ...COL_HEAD_STYLE, width: 56, flexShrink: 0, textAlign: 'right' }}>Mean</span>
        </div>

        {competitors.map((member) => {
          const isYou = ownJoinOrdinal !== null && member.joinOrdinal === ownJoinOrdinal;
          return (
            <div
              key={member.player}
              data-testid={`standings-row-${member.player}`}
              style={isYou ? { ...TABLE_ROW_STYLE, background: 'var(--accent-soft)' } : TABLE_ROW_STYLE}
            >
              <span data-testid={`standings-placement-${member.player}`} style={ORDINAL_STYLE}>
                {/* Rank/placement wording is required here (`client-rounds-standings`'s inversion
                    of R3) — a dash only for the unreachable case of a competing member the server
                    has not yet ranked (before any round is graded, a state this list never renders
                    in — see the `noGradedRound` arm above). */}
                {member.rank === null ? '—' : ordinal(member.rank)}
              </span>
              <span style={NAME_CELL_STYLE}>
                {isYou ? <span style={YOU_STYLE}>You</span> : member.displayName}
              </span>
              {roundNumbers.map((roundNo) => {
                const score = scoreForRound(member, roundNo);
                return (
                  <span key={roundNo} style={{ ...SCORE_CELL_STYLE, width: 40 }}>
                    {score === null ? '—' : groupDigits(score)}
                  </span>
                );
              })}
              <span
                data-testid={`standings-mean-${member.player}`}
                style={isYou ? { ...MEAN_CELL_STYLE, color: 'var(--accent-strong)' } : MEAN_CELL_STYLE}
              >
                {member.mean === null ? '—' : groupDigits(Math.round(member.mean))}
              </span>
            </div>
          );
        })}
      </div>

      {spectators.length > 0 && (
        <div data-testid="standings-spectators" style={SPECTATOR_LIST_STYLE}>
          {spectators.map((member) => (
            <div
              key={member.player}
              data-testid={`standings-spectator-${member.player}`}
              style={SPECTATOR_ROW_STYLE}
            >
              <span style={NAME_CELL_STYLE}>{member.displayName}</span>
              <span style={SPECTATOR_TAG_STYLE}>Spectator</span>
            </div>
          ))}
        </div>
      )}

      {/* **UI/UX audit F-16.** The count/plural agreement was already right; the *phrasing* was not.
          At n=1 this read "The mean is over the 1 round graded so far, so it moves every round" —
          "the 1 round" is not idiomatic, and "moves every round" argues against a number the player
          can see is a single one. Special-cased rather than patched with a plural helper, because the
          two cases want genuinely different sentences. */}
      <div data-testid="standings-note" style={NOTE_BOX_STYLE}>
        {standings.gradedRounds === 1
          ? 'Only one round has been graded, so this mean will move as more are played — a lead now is not a result.'
          : `The mean is over the ${standings.gradedRounds} rounds graded so far, so it moves every round — a lead now is not a result.`}
      </div>

      {roundNumbers.length > 0 && (
        <div data-testid="standings-rounds" style={ROUNDS_SECTION_STYLE}>
          <div style={ROUNDS_CAP_STYLE}>Rounds</div>
          {roundNumbers.map((roundNo) => {
            // `null` whenever `ownColonyId` is `null`, or whenever D4's cross-check disagrees
            // with it — the fail-safe firing here is "no medal", never a mislabelled one.
            const own = roster.ownPlacementFor(roundNo);
            const isEvicted = evicted.has(roundNo);
            return (
              <div key={roundNo} data-testid={`standings-round-${roundNo}`} style={ROUND_CARD_STYLE}>
                <span style={MEDAL_STYLE}>
                  {own === null || own.placement === null ? '—' : ordinal(own.placement)}
                </span>
                <span style={ROUND_TEXT_STYLE}>
                  <b style={ROUND_TEXT_TITLE_STYLE}>
                    Round {roundNo}
                    {own !== null && own.score !== null ? ` · ${groupDigits(own.score)} bees` : ''}
                  </b>
                  {/* Calm, never alarm chrome: the round stays watchable at coarse and its
                      placement is unaffected — retention states a condition, not a failure
                      (design.md's Non-Goal: this reads an injected signal, never re-derives one). */}
                  {isEvicted && <span>Detail for this round has been reclaimed — still watchable at coarse.</span>}
                </span>
                <button
                  type="button"
                  data-testid={`standings-watch-round-${roundNo}`}
                  style={WATCH_BUTTON_STYLE}
                  onClick={() => onWatchRound?.(roundNo)}
                >
                  Watch
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const SCREEN_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--surface-app)',
};

const HEADER_STYLE: CSSProperties = {
  padding: '14px 14px 10px',
  background: 'var(--hud-bg)',
  boxShadow: 'inset 0 -1px 0 var(--line)',
  flexShrink: 0,
};

const KICKER_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 9,
  letterSpacing: 'var(--tracking-caps, 0.08em)',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const TITLE_STYLE: CSSProperties = {
  margin: '2px 0 0',
  textAlign: 'left',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 23,
  color: 'var(--ink-1)',
};

const SUBTITLE_STYLE: CSSProperties = {
  marginTop: 3,
  fontSize: 11.5,
  color: 'var(--ink-2)',
};

// 78 = the 64px back bar + 14 breathing room, the sketch's own comment: the back bar sits
// absolutely positioned at the bottom, so without this the last round card renders behind it.
/**
 * **UI/UX audit F-11.** The table was full-bleed at 1185px, which put the player's name at the far
 * left and their score at the far right across ~1100px of empty space — the row-to-value association
 * the table exists for. Capped at 880px and centred: wider than Home's form column because a table
 * genuinely wants more room, narrow enough that a row still scans as one line. `width: 100%` keeps
 * phone layout untouched.
 */
const BODY_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: '12px 14px 78px',
  width: '100%',
  maxWidth: 880,
  marginInline: 'auto',
  boxSizing: 'border-box',
};

const NOTE_STYLE: CSSProperties = {
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
};

/** Fills the (already `flex: 1`) content area and centers whatever empty-state pill it holds —
 * the same treatment `SandboxScreen`'s empty-world hint uses for "nothing to show yet". */
const EMPTY_STATE_STYLE: CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const EMPTY_STATE_PILL_STYLE: CSSProperties = {
  maxWidth: 420,
  padding: '8px 14px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--hud-bg)',
  border: '1px solid var(--line-strong)',
  boxShadow: 'var(--shadow-hud)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
};

const ALERT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '10px 12px',
  borderRadius: 'var(--r-md)',
  background: 'var(--danger-soft)',
  border: '1px solid var(--danger)',
  color: '#7E2C1B',
  fontSize: 12.5,
  lineHeight: 1.4,
};

const ALERT_TITLE_STYLE: CSSProperties = { fontWeight: 800 };

const TABLE_STYLE: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1.5px solid var(--line-strong)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--shadow-card)',
  overflow: 'hidden',
};

const TABLE_HEAD_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 11px',
  background: 'var(--paper-1)',
  boxShadow: 'inset 0 -1px 0 var(--line)',
};

const COL_HEAD_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 8,
  letterSpacing: 'var(--tracking-caps, 0.08em)',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const TABLE_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 11px',
  borderTop: '1px solid var(--line)',
};

const ORDINAL_STYLE: CSSProperties = {
  width: 30,
  flexShrink: 0,
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 15,
  color: 'var(--ink-1)',
};

const NAME_CELL_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13.5,
  color: 'var(--ink-1)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const YOU_STYLE: CSSProperties = { color: 'var(--accent-strong)' };

const SCORE_CELL_STYLE: CSSProperties = {
  flexShrink: 0,
  textAlign: 'right',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 12.5,
  color: 'var(--ink-2)',
  fontVariantNumeric: 'tabular-nums',
};

const MEAN_CELL_STYLE: CSSProperties = {
  width: 56,
  flexShrink: 0,
  textAlign: 'right',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 17,
  color: 'var(--ink-1)',
  fontVariantNumeric: 'tabular-nums',
};

const SPECTATOR_LIST_STYLE: CSSProperties = {
  marginTop: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const SPECTATOR_ROW_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 11px',
  borderRadius: 'var(--r-md)',
  background: 'var(--paper-1)',
  border: '1px dashed var(--line)',
};

const SPECTATOR_TAG_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 9,
  letterSpacing: 'var(--tracking-caps, 0.08em)',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const NOTE_BOX_STYLE: CSSProperties = {
  marginTop: 10,
  padding: '9px 11px',
  borderRadius: 'var(--r-md)',
  background: 'var(--paper-1)',
  border: '1px solid var(--line)',
  fontSize: 11,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
};

const ROUNDS_SECTION_STYLE: CSSProperties = { marginTop: 12 };

const ROUNDS_CAP_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 9,
  letterSpacing: 'var(--tracking-caps, 0.08em)',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const ROUND_CARD_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 11px',
  borderRadius: 'var(--r-md)',
  background: 'var(--surface-card)',
  border: '1.5px solid var(--line)',
  boxShadow: 'var(--shadow-card)',
  marginBottom: 7,
};

const MEDAL_STYLE: CSSProperties = {
  width: 34,
  height: 34,
  flexShrink: 0,
  borderRadius: 17,
  background: 'var(--paper-1)',
  border: '1.5px solid var(--line-strong)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--ink-2)',
};

const ROUND_TEXT_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontSize: 11,
  lineHeight: 1.35,
  color: 'var(--text-muted)',
};

const ROUND_TEXT_TITLE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--ink-1)',
};

// ≥ 44px hit target (UI.md's rule, restated in design.md D10 for every new rail/row this change
// adds).
const WATCH_BUTTON_STYLE: CSSProperties = {
  flexShrink: 0,
  minHeight: 44,
  padding: '0 13px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--surface-card)',
  border: '1.5px solid var(--line-strong)',
  color: 'var(--ink-2)',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 12,
  cursor: 'pointer',
};

const BACK_BAR_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 64,
  background: 'var(--hud-bg)',
  boxShadow: 'inset 0 1.5px 0 var(--hud-line)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 14px',
};

const BACK_BUTTON_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 44,
  borderRadius: 'var(--r-pill)',
  background: 'var(--accent-fill)',
  border: '1px solid var(--accent-strong)',
  color: '#FFFDF6',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 14,
  cursor: 'pointer',
};
